import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { RouteKey } from '@elegant-router/types';
import { SetupStoreId } from '@/store/plugins';
import { localStg } from '@/utils/storage';

/** 标签页 */
interface Tab {
  /** 标签id */
  id: string;
  /** 标签标题 */
  title: string;
  /** 标签路由名称 */
  name: string;
  /** 标签路由路径 */
  path: string;
  /** 标签路由查询参数 */
  query?: Record<string, string>;
  /** 标签路由params参数 */
  params?: Record<string, string>;
  /** 标签页缓存 */
  keepAlive?: boolean;
  /** 标签图标 */
  icon?: string;
  /** 固定标签 */
  pinned?: boolean;
}

/** 标签页合集 */
interface Tabs {
  /** 访问过的标签页 */
  visited: Tab[];
  /** 收藏的标签页 */
  starred: Tab[];
}

export const useTabStore = defineStore(SetupStoreId.Tab, () => {
  const route = useRoute();
  const router = useRouter();
  
  /** 标签页列表 */
  const tabs = reactive<Tabs>({
    visited: [],
    starred: []
  });
  
  /** 激活的标签页 */
  const activeTab = ref<Tab | null>(null);
  
  /** 激活的标签索引 */
  const activeTabIndex = computed(() => {
    if (!activeTab.value) return -1;
    return tabs.visited.findIndex(tab => tab.id === activeTab.value?.id);
  });
  
  /** 是否存在标签页 */
  function hasTab(path: string, name?: string) {
    return tabs.visited.some(tab => {
      if (name) {
        return tab.name === name && tab.path === path;
      }
      return tab.path === path;
    });
  }
  
  /** 初始化首页标签 */
  function initHomeTab() {
    const home: RouteKey = 'root';
    const homePath = '/';
    
    const existTab = tabs.visited.some(tab => tab.name === home && tab.path === homePath);
    
    if (!existTab) {
      const tab: Tab = {
        id: `${home}-${homePath}`,
        title: '首页',
        name: home,
        path: homePath,
        keepAlive: true,
        pinned: true
      };
      
      // 添加到标签列表
      tabs.visited.unshift(tab);
      
      // 缓存标签页
      localStg.set('visitedTabs', tabs.visited);
    }
  }
  
  /**
   * 添加标签页
   * @param tab 标签页
   */
  function addTab(tab: Tab) {
    if (!hasTab(tab.path, tab.name)) {
      tabs.visited.push(tab);
      activeTab.value = tab;
      
      // 缓存标签页
      localStg.set('visitedTabs', tabs.visited);
    } else {
      // 激活已存在的标签页
      const existTab = tabs.visited.find(item => item.path === tab.path && item.name === tab.name);
      if (existTab) {
        activeTab.value = existTab;
      }
    }
  }
  
  /**
   * 移除标签页
   * @param id 标签id
   */
  function removeTab(id: string) {
    const index = tabs.visited.findIndex(tab => tab.id === id);
    
    if (index > -1) {
      // 如果移除的是激活的标签页，则激活相邻的标签页
      if (tabs.visited[index].id === activeTab.value?.id) {
        let nextIndex = index + 1;
        
        if (nextIndex >= tabs.visited.length) {
          nextIndex = index - 1;
        }
        
        if (nextIndex >= 0) {
          activeTab.value = tabs.visited[nextIndex];
          
          // 跳转到激活的标签页
          const { path } = activeTab.value;
          router.push(path);
        } else {
          activeTab.value = null;
        }
      }
      
      // 移除标签页
      tabs.visited.splice(index, 1);
      
      // 缓存标签页
      localStg.set('visitedTabs', tabs.visited);
    }
  }
  
  /**
   * 缓存标签页
   */
  function cacheTabs() {
    localStg.set('visitedTabs', tabs.visited);
  }
  
  /**
   * 根据路由生成标签页
   * @param route 路由
   */
  function createTabByRoute(route: RouteLocationNormalizedLoaded): Tab {
    const id = `${String(route.name)}-${route.path}`;
    const title = route.meta?.title as string || id;
    const { name, path, query, params } = route;
    const keepAlive = Boolean(route.meta?.keepAlive);
    const icon = route.meta?.icon as string;
    
    const tab: Tab = {
      id,
      title,
      name: String(name),
      path,
      query: query as Record<string, string>,
      params: params as Record<string, string>,
      keepAlive,
      icon
    };
    
    return tab;
  }
  
  /**
   * 根据当前路由设置激活的标签页
   */
  function setActiveTabByRoute() {
    const currentRoute = route;
    const currentTab = createTabByRoute(currentRoute);
    
    activeTab.value = currentTab;
    
    if (!hasTab(currentTab.path, currentTab.name)) {
      addTab(currentTab);
    }
  }
  
  /**
   * 固定/取消固定标签页
   * @param id 标签id
   */
  function togglePinTab(id: string) {
    const index = tabs.visited.findIndex(tab => tab.id === id);
    
    if (index > -1) {
      const tab = tabs.visited[index];
      tab.pinned = !tab.pinned;
      
      // 缓存标签页
      localStg.set('visitedTabs', tabs.visited);
    }
  }
  
  return {
    tabs,
    activeTab,
    activeTabIndex,
    hasTab,
    initHomeTab,
    addTab,
    removeTab,
    cacheTabs,
    createTabByRoute,
    setActiveTabByRoute,
    togglePinTab
  };
}); 